import { collection, query, where, getDocs, limit, orderBy, startAt, endAt } from 'firebase/firestore';
import { db } from './firebase';

// Cache para almacenar todos los medicamentos
let medicamentosCache = null;
let cacheTimestamp = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutos

// Función para limpiar el cache
export const clearMedicamentosCache = () => {
  medicamentosCache = null;
  cacheTimestamp = null;
  console.log('Cache de medicamentos limpiado');
};

// Búsqueda directa en Firestore con cache
export const searchMedicamentosDirectly = async (searchText) => {
  if (!searchText || searchText.length < 2) {
    return [];
  }

  const searchTextUpper = searchText.toUpperCase();
  const searchTextLower = searchText.toLowerCase();
  const searchTextNormalized = searchText.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toUpperCase();
  
  try {
    const results = [];
    const foundIds = new Set();
    
    // Verificar si necesitamos actualizar el cache
    const now = Date.now();
    const needsUpdate = !medicamentosCache || !cacheTimestamp || (now - cacheTimestamp > CACHE_DURATION);
    
    let allMedicamentos;
    
    if (needsUpdate) {
      console.log('Actualizando cache de medicamentos...');
      // Buscar todos los medicamentos sin límite
      const allMedicamentosQuery = query(
        collection(db, 'medicamentos')
        // Sin límite para buscar entre todos los productos
      );
      
      const snapshot = await getDocs(allMedicamentosQuery);
      allMedicamentos = [];
      
      snapshot.forEach((doc) => {
        const data = doc.data();
        allMedicamentos.push({
          id: doc.id,
          ...data
        });
      });
      
      // Actualizar cache
      medicamentosCache = allMedicamentos;
      cacheTimestamp = now;
      
      console.log(`Cache actualizado con ${allMedicamentos.length} medicamentos`);
    } else {
      allMedicamentos = medicamentosCache;
      console.log(`Usando cache con ${allMedicamentos.length} medicamentos`);
    }
    
    console.log(`Buscando "${searchText}" entre ${allMedicamentos.length} medicamentos`);
    
    // Debug: Buscar específicamente electrolit y viagra
    if (searchTextLower.includes('electrolit') || searchTextLower.includes('viagra')) {
      // Buscar con diferentes variaciones
      const ejemplos = allMedicamentos.filter(med => {
        const nombre = (med.nombre || '').toLowerCase();
        const nombreSinEspacios = nombre.replace(/[\s-]/g, '');
        const busquedaSinEspacios = searchTextLower.replace(/[\s-]/g, '');
        
        return nombre.includes('electro') || 
               nombre.includes('viagra') || 
               nombre.includes('sildenafil') ||
               nombreSinEspacios.includes(busquedaSinEspacios) ||
               nombre.includes('suero') ||
               nombre.includes('rehidratante');
      }).slice(0, 10);
      
      console.log('Debug - Productos que contienen "electro":', ejemplos.map(e => ({
        nombre: e.nombre,
        sustancia: e.sustancia,
        laboratorio: e.proveedor,
        descripcion: e.descripcion
      })));
      
      // Buscar específicamente variaciones de electrolit
      const electrolitVariations = allMedicamentos.filter(med => {
        const nombre = (med.nombre || '').toLowerCase();
        return nombre.includes('electro') && nombre.includes('lit');
      }).slice(0, 5);
      
      console.log('Debug - Variaciones de electrolit encontradas:', electrolitVariations.map(e => e.nombre));
    }
    
    // Filtrar por nombre, sustancia, laboratorio, descripción y presentación
    allMedicamentos.forEach((med) => {
      const nombre = (med.nombre || '').toUpperCase();
      const nombreNormalized = nombre.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
      const sustancia = (med.sustancia || '').toUpperCase();
      const laboratorio = (med.proveedor || '').toUpperCase();
      const descripcion = (med.descripcion || '').toUpperCase();
      const presentacion = (med.presentacion || '').toUpperCase();
      const codigoBarras = med.codigoBarras || '';
      
      // Buscar en todos los campos posibles
      const searchIn = `${nombre} ${sustancia} ${laboratorio} ${descripcion} ${presentacion}`;
      const searchInNormalized = searchIn.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
      
      // Crear versiones sin espacios ni guiones para búsqueda más flexible
      const nombreSinEspacios = nombre.replace(/[\s-]/g, '');
      const searchTextSinEspacios = searchTextUpper.replace(/[\s-]/g, '');
      const searchInSinEspacios = searchIn.replace(/[\s-]/g, '');
      
      let relevance = 10; // Por defecto, baja relevancia
      
      // Coincidencia exacta con código de barras
      if (codigoBarras === searchText || codigoBarras.includes(searchText)) {
        relevance = 0;
      }
      // Nombre exacto
      else if (nombre === searchTextUpper || nombreSinEspacios === searchTextSinEspacios) {
        relevance = 0.5;
      }
      // Nombre empieza con el texto buscado
      else if (nombre.startsWith(searchTextUpper) || nombreNormalized.startsWith(searchTextNormalized) || 
               nombreSinEspacios.startsWith(searchTextSinEspacios)) {
        relevance = 1;
      }
      // Nombre contiene el texto buscado (con o sin espacios)
      else if (nombre.includes(searchTextUpper) || nombreNormalized.includes(searchTextNormalized) ||
               nombreSinEspacios.includes(searchTextSinEspacios)) {
        relevance = 2;
      }
      // Cualquier campo contiene el texto buscado
      else if (searchIn.includes(searchTextUpper) || searchInNormalized.includes(searchTextNormalized) ||
               searchInSinEspacios.includes(searchTextSinEspacios)) {
        relevance = 3;
      }
      
      // También buscar por palabras separadas (ej: "ELECTRO LIT" encuentra "ELECTROLIT")
      const searchWords = searchTextUpper.split(/[\s-]+/).filter(w => w.length > 1);
      if (searchWords.length > 0 && searchWords.every(word => searchIn.includes(word))) {
        relevance = Math.min(relevance, 4);
      }
      
      if (relevance < 10 && !foundIds.has(med.id)) {
        results.push({
          ...med,
          relevance
        });
        foundIds.add(med.id);
      }
    });
    
    
    // Buscar también por palabras clave comunes y sus variaciones
    if (searchTextLower.length >= 2) {
      // Lista de palabras clave comunes y sus variaciones
      const keywords = {
        'dolex': ['DOLEX', 'ACETAMINOFEN', 'PARACETAMOL', 'TYLENOL'],
        'aspirina': ['ASPIRINA', 'ACIDO ACETILSALICILICO', 'ASA', 'ASPIRIN'],
        'acetaminofen': ['ACETAMINOFEN', 'PARACETAMOL', 'ACETAMINOPHEN', 'DOLEX', 'TYLENOL'],
        'paracetamol': ['PARACETAMOL', 'ACETAMINOFEN', 'DOLEX', 'TYLENOL'],
        'ibuprofeno': ['IBUPROFENO', 'IBUPROFEN', 'ADVIL', 'MOTRIN', 'BRUFEN'],
        'amoxicilina': ['AMOXICILINA', 'AMOXICILLIN', 'AMOXIL'],
        'diclofenaco': ['DICLOFENACO', 'DICLOFENAC', 'VOLTAREN', 'CATAFLAM'],
        'omeprazol': ['OMEPRAZOL', 'OMEPRAZOLE', 'PRILOSEC'],
        'metformina': ['METFORMINA', 'METFORMIN', 'GLUCOPHAGE'],
        'losartan': ['LOSARTAN', 'COZAAR'],
        'atorvastatina': ['ATORVASTATINA', 'ATORVASTATIN', 'LIPITOR'],
        'loratadina': ['LORATADINA', 'LORATADINE', 'CLARITIN'],
        'cetirizina': ['CETIRIZINA', 'CETIRIZINE', 'ZYRTEC'],
        'ranitidina': ['RANITIDINA', 'RANITIDINE', 'ZANTAC'],
        'electrolit': ['ELECTROLIT', 'SUERO ORAL', 'REHIDRATANTE', 'PEDIALYTE'],
        'viagra': ['VIAGRA', 'SILDENAFIL', 'SILDENAFILO'],
        'sildenafil': ['SILDENAFIL', 'SILDENAFILO', 'VIAGRA']
      };
      
      // Buscar si el texto coincide con alguna palabra clave
      let searchTerms = [searchTextUpper];
      for (const [key, values] of Object.entries(keywords)) {
        if (key.toUpperCase().includes(searchTextUpper) || searchTextUpper.includes(key.toUpperCase())) {
          searchTerms = [...searchTerms, ...values];
          break;
        }
      }
      
      // Buscar productos que coincidan con los términos relacionados
      allMedicamentos.forEach((med) => {
        if (foundIds.has(med.id)) return; // Ya lo agregamos
        
        const nombre = (med.nombre || '').toUpperCase();
        const sustancia = (med.sustancia || '').toUpperCase();
        const descripcion = (med.descripcion || '').toUpperCase();
        
        // Verificar si algún término relacionado coincide
        for (const term of searchTerms) {
          if (nombre.includes(term) || sustancia.includes(term) || descripcion.includes(term)) {
            results.push({
              ...med,
              relevance: 5 // Relevancia media-baja para términos relacionados
            });
            foundIds.add(med.id);
            break;
          }
        }
      });
    }
    
    // Ordenar por relevancia
    results.sort((a, b) => {
      if (a.relevance !== b.relevance) {
        return a.relevance - b.relevance;
      }
      return (a.nombre || '').localeCompare(b.nombre || '');
    });
    
    console.log(`Encontrados ${results.length} resultados para "${searchText}"`);
    
    // Remover el campo de relevancia antes de devolver
    // Aumentar el límite de resultados devueltos a 30
    return results.map(({ relevance, ...rest }) => rest).slice(0, 30);
    
  } catch (error) {
    console.error('Error en búsqueda directa:', error);
    
    // Si hay error (probablemente falta de índice), hacer búsqueda simple
    try {
      const fallbackQuery = query(
        collection(db, 'medicamentos')
        // Sin límite para buscar entre todos los productos
      );
      
      const snapshot = await getDocs(fallbackQuery);
      const allResults = [];
      
      snapshot.forEach((doc) => {
        const data = doc.data();
        const nombre = (data.nombre || '').toUpperCase();
        const nombreNormalized = nombre.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
        const nombreSinEspacios = nombre.replace(/[\s-]/g, '');
        const searchTextSinEspacios = searchTextUpper.replace(/[\s-]/g, '');
        const codigoBarras = data.codigoBarras || '';
        const laboratorio = (data.proveedor || '').toUpperCase();
        const sustancia = (data.sustancia || '').toUpperCase();
        const descripcion = (data.descripcion || '').toUpperCase();
        
        // Búsqueda más flexible
        if (nombre.includes(searchTextUpper) || 
            nombreNormalized.includes(searchTextNormalized) ||
            nombreSinEspacios.includes(searchTextSinEspacios) ||
            codigoBarras.includes(searchText) ||
            laboratorio.includes(searchTextUpper) ||
            sustancia.includes(searchTextUpper) ||
            descripcion.includes(searchTextUpper)) {
          allResults.push({
            id: doc.id,
            ...data
          });
        }
      });
      
      // Ordenar por relevancia
      allResults.sort((a, b) => {
        const aNombre = (a.nombre || '').toUpperCase();
        const bNombre = (b.nombre || '').toUpperCase();
        
        // Priorizar coincidencias que empiezan con el texto buscado
        const aStartsWith = aNombre.startsWith(searchTextUpper);
        const bStartsWith = bNombre.startsWith(searchTextUpper);
        
        if (aStartsWith && !bStartsWith) return -1;
        if (!aStartsWith && bStartsWith) return 1;
        
        return aNombre.localeCompare(bNombre);
      });
      
      console.log(`Búsqueda de respaldo: ${allResults.length} resultados para "${searchText}"`);
      return allResults.slice(0, 30);
    } catch (fallbackError) {
      console.error('Error en búsqueda de respaldo:', fallbackError);
      return [];
    }
  }
};